import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useNotification } from '../contexts/NotificationContext';
import MediaUploader from '../components/campaigns/MediaUploader';
import TemplatePreview from '../components/campaigns/TemplatePreview';
import TemplateSelector from '../components/campaigns/TemplateSelector';
import DateTimeInlinePicker from '../components/DateTimeInlinePicker';
import InlineTimePicker from '../components/InlineTimePicker';
import {
  useGetCampaignQuery,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useGetTemplatesQuery,
  useGetContactsQuery,
  useGetContactGroupsQuery,
  Template
} from '../store/services/whatsappApi';
import { ExtendedTemplate } from '../types/template';

// WhatsApp pricing data by country code
const WHATSAPP_PRICING: { [key: string]: number } = {
  // North America
  '1': 0.0085,    // USA, Canada

  // Western Europe
  '44': 0.0085,   // UK
  '33': 0.0085,   // France
  '49': 0.0085,   // Germany
  '39': 0.0085,   // Italy
  '34': 0.0085,   // Spain

  // Brazil
  '55': 0.0085,

  // India
  '91': 0.005,

  // Africa
  '254': 0.005,   // Kenya
  '255': 0.005,   // Tanzania
  '256': 0.005,   // Uganda
  '27': 0.005,    // South Africa
  '233': 0.005,   // Ghana
  '234': 0.005,   // Nigeria
  '20': 0.020,    // Egypt

  // Asia Pacific
  '65': 0.005,    // Singapore
  '60': 0.005,    // Malaysia
  '62': 0.005,    // Indonesia
  '66': 0.005,    // Thailand
  '84': 0.005,    // Vietnam
  '63': 0.015,    // Philippines
  '82': 0.015,    // South Korea
  '81': 0.015,    // Japan

  // Default for other countries
  'default': 0.0085
};

// Extract country code from phone number
const extractCountryCode = (phoneNumber: string): string => {
  // Remove any non-numeric characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // Handle international format (+country code)
  if (cleaned.startsWith('+')) {
    // Common country codes (1-3 digits)
    if (cleaned.match(/^\+1/)) return '1';      // North America
    if (cleaned.match(/^\+44/)) return '44';    // UK
    if (cleaned.match(/^\+33/)) return '33';    // France
    if (cleaned.match(/^\+49/)) return '49';    // Germany
    if (cleaned.match(/^\+39/)) return '39';    // Italy
    if (cleaned.match(/^\+34/)) return '34';    // Spain
    if (cleaned.match(/^\+55/)) return '55';    // Brazil
    if (cleaned.match(/^\+91/)) return '91';    // India
    if (cleaned.match(/^\+254/)) return '254';  // Kenya
    if (cleaned.match(/^\+255/)) return '255';  // Tanzania
    if (cleaned.match(/^\+256/)) return '256';  // Uganda
    if (cleaned.match(/^\+27/)) return '27';    // South Africa
    if (cleaned.match(/^\+233/)) return '233';  // Ghana
    if (cleaned.match(/^\+234/)) return '234';  // Nigeria
    if (cleaned.match(/^\+20/)) return '20';    // Egypt
    if (cleaned.match(/^\+65/)) return '65';    // Singapore
    if (cleaned.match(/^\+60/)) return '60';    // Malaysia
    if (cleaned.match(/^\+62/)) return '62';    // Indonesia
    if (cleaned.match(/^\+66/)) return '66';    // Thailand
    if (cleaned.match(/^\+84/)) return '84';    // Vietnam
    if (cleaned.match(/^\+63/)) return '63';    // Philippines
    if (cleaned.match(/^\+82/)) return '82';    // South Korea
    if (cleaned.match(/^\+81/)) return '81';    // Japan
  }

  // If no country code found, return default
  return 'default';
};

// Calculate estimated campaign cost
const calculateCampaignCost = (contactGroupIds: string[], contacts: any[], contactGroups: any[]): { totalCost: number; breakdown: { [key: string]: { count: number; cost: number } } } => {
  const countryCounts: { [key: string]: number } = {};

  // Process selected contacts and groups
  contactGroupIds.forEach(id => {
    if (id.startsWith('contact:')) {
      const contactId = id.split(':')[1];
      const contact = contacts?.find(c => c.id === parseInt(contactId));
      if (contact) {
        const countryCode = extractCountryCode(contact.phone_number);
        countryCounts[countryCode] = (countryCounts[countryCode] || 0) + 1;
      }
    } else if (id.startsWith('group:')) {
      const groupId = id.split(':')[1];
      const group = contactGroups?.find(g => g.id === parseInt(groupId));
      if (group) {
        // For groups, we'd need to get individual contacts, but for estimation we'll use a representative sample
        // For now, assume average distribution or use group location if available
        const estimatedCountry = 'default'; // Could be enhanced to use group metadata
        const memberCount = group.memberCount || 1;
        countryCounts[estimatedCountry] = (countryCounts[estimatedCountry] || 0) + memberCount;
      }
    }
  });

  // Calculate costs
  const breakdown: { [key: string]: { count: number; cost: number } } = {};
  let totalCost = 0;

  Object.entries(countryCounts).forEach(([countryCode, count]) => {
    const costPerConversation = WHATSAPP_PRICING[countryCode] || WHATSAPP_PRICING.default;
    const countryCost = count * costPerConversation;
    breakdown[countryCode] = { count, cost: countryCost };
    totalCost += countryCost;
  });

  return { totalCost, breakdown };
};

type CampaignFormData = {
  name: string;
  description: string;
  templateId: string;
  scheduledAt?: string;
  contactGroupIds: string[];
  status?: string;
  mediaHandle?: string;
  
  // Recurring campaign fields
  isRecurring?: number; // 0: one-time, 1: recurring
  recurrenceType?: string; // daily, weekly, monthly
  recurrenceDays?: number[]; // Days for weekly (0-6) or monthly (1-31)
  recurrenceHour?: number; // Hour of day (0-23)
  recurrenceMinute?: number; // Minute (0-59)
  recurrenceEndDate?: string; // When to stop recurring campaigns
};

const CampaignCreate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  // Form and state management
  const { register, handleSubmit, reset, watch, control, setValue, formState: { errors } } = useForm<CampaignFormData>();
  const { data: campaignData, isLoading: isCampaignLoading } = useGetCampaignQuery(id ? parseInt(id) : 0, { skip: !isEditing });
  const { data: templates, isLoading: isTemplatesLoading } = useGetTemplatesQuery({ status: "APPROVED" });
  
  // State for improved recipient selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('contacts'); // 'contacts' or 'groups'
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [recipientCount, setRecipientCount] = useState(0);

  // State for campaign cost estimation
  const [campaignCost, setCampaignCost] = useState<{ totalCost: number; breakdown: { [key: string]: { count: number; cost: number } } } | null>(null);
  
  // State for template preview
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatePreview, setTemplatePreview] = useState<any>(null);
  
  // State for media upload
  const [mediaHandle, setMediaHandle] = useState<string>('');
  const [mediaType, setMediaType] = useState<string>('image');
  const [phoneNumberId, /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    setPhoneNumberId] = useState<string>('');
  
  // Scheduling state
  const [scheduleImmediately, setScheduleImmediately] = useState(false);
  
  // Recurring campaign state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [recurrenceHour, setRecurrenceHour] = useState(9); // Default to 9 AM
  const [recurrenceMinute, setRecurrenceMinute] = useState(0);

  const { data: contacts } = useGetContactsQuery({ limit: 10000 });
  const { data: contactGroups } = useGetContactGroupsQuery();
  const [createCampaign, { isLoading: isCreating }] = useCreateCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  
  // Access notification context
  const { showNotification } = useNotification();

  // Extract variables from template content using the {{variable}} syntax
  const extractTemplateVariables = (template: any): string[] => {
    if (!template) return [];
    
    const variables: Set<string> = new Set();
    
    // Check all components for variables
    template.components?.forEach((component: any) => {
      if (component.text) {
        const matches = component.text.match(/{{([^}]+)}}/g) || [];
        matches.forEach((match: string) => {
          // Extract just the variable name without braces
          const variableName = match.replace('{{', '').replace('}}', '');
          variables.add(variableName);
        });
      }
    });
    
    return Array.from(variables);
  };

  // Handle template selection change
  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    if (!templateId) {
      setTemplatePreview(null);
      setValue('templateId', '');
      return;
    }
    
    const selectedTemplate = templates?.find((t: Template) => t.id.toString() === templateId) as ExtendedTemplate;
    if (selectedTemplate) {
      // Extract variables from template content
      const variables = extractTemplateVariables(selectedTemplate);
      
      // Check if this is a media template and set the media type
      if (selectedTemplate.header_format && selectedTemplate.header_format !== 'TEXT') {
        setMediaType(selectedTemplate.header_format.toLowerCase());
      } else {
        setMediaType('image'); // Default to image
        setMediaHandle(''); // Clear media handle for non-media templates
      }
      
      // Set the template preview with variables
      setTemplatePreview({
        ...selectedTemplate,
        variables
      });
      setSelectedTemplateId(templateId);
      setValue('templateId', templateId);
    }
  };
  
  const handleMediaUploaded = (newMediaHandle: string, newMediaType: string) => {
    setMediaHandle(newMediaHandle);
    setMediaType(newMediaType);
    
    // Update template preview with the new media handle
    if (templatePreview) {
      setTemplatePreview({
        ...templatePreview,
        media_handle: newMediaHandle
      });
    }
  };

  const renderTemplateSelectionSection = () => {
    return (
      <div className="mb-8 border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Template Selection</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="templateId">
                Message Template *
              </label>
              <TemplateSelector
                templates={templates || []}
                selectedTemplateId={selectedTemplateId || watch('templateId') || ''}
                onTemplateSelect={(templateId) => {
                  const event = {
                    target: { value: templateId }
                  } as React.ChangeEvent<HTMLSelectElement>;
                  handleTemplateChange(event);
                }}
              />
              <input
                type="hidden"
                {...register("templateId", { required: "Template selection is required" })}
                value={selectedTemplateId || watch('templateId') || ''}
              />
              {errors.templateId && <p className="text-red-500 text-xs mt-1">{errors.templateId.message}</p>}
            </div>
            
            {/* Show media uploader only for templates with media headers */}
            {templatePreview && templatePreview.header_format && templatePreview.header_format !== 'TEXT' && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Upload {templatePreview.header_format.toLowerCase()} for template header
                </label>
                <div className="flex items-center">
                  <MediaUploader 
                    onMediaUploaded={handleMediaUploaded} 
                    mediaType={mediaType}
                    className="mr-2"
                    useWhatsAppApi={true}
                    phoneNumberId={phoneNumberId || '783696321494612'} // Use default phone number ID if not set
                  />
                  {mediaHandle && (
                    <span className="text-sm text-green-600">
                      <svg className="inline-block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Media uploaded successfully
                    </span>
                  )}
                </div>
                <input 
                  type="hidden" 
                  {...register("mediaHandle")} 
                  value={mediaHandle || ''} 
                />
              </div>
            )}
            
            {templatePreview?.variables && templatePreview.variables.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-medium mb-2">Template Variables</h4>
                <p className="text-sm text-gray-600 mb-3">
                  This template contains variables that will be replaced with actual values when sending messages.
                </p>
                
                <div className="space-y-3">
                  {templatePreview.variables.map((variable: string, index: number) => (
                    <div key={index} className="flex items-center">
                      <span className="text-sm font-medium w-1/3">{variable}</span>
                      <input
                        type="text"
                        placeholder={`Value for ${variable}`}
                        className="shadow appearance-none border rounded w-2/3 py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Preview
            </label>
            <div className="flex-grow bg-gray-50 rounded-lg p-4 flex items-center justify-center">
              <TemplatePreview template={templatePreview} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Filter contacts and groups based on search term
  const filteredContacts = contacts?.contacts?.filter(contact =>
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone_number?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  const filteredGroups = contactGroups?.filter((group: any) => 
    group.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  // Calculate total recipient count and campaign cost when selections change
  useEffect(() => {
    let count = selectedContacts.length;

    // Add count of contacts in each selected group
    selectedGroups.forEach(groupId => {
      const group = contactGroups?.find((g: any) => g.id === groupId);
      if (group && group.memberCount) {
        count += group.memberCount;
      }
    });

    setRecipientCount(count);

    // Calculate campaign cost
    const contactGroupIds = [
      ...selectedContacts.map(id => `contact:${id}`),
      ...selectedGroups.map(id => `group:${id}`)
    ];

    if (contactGroupIds.length > 0) {
      const cost = calculateCampaignCost(contactGroupIds, contacts?.contacts || [], contactGroups || []);
      setCampaignCost(cost);
    } else {
      setCampaignCost(null);
    }
  }, [selectedContacts, selectedGroups, contactGroups, contacts]);
  
  // Synchronize selected contacts and groups with form data
  useEffect(() => {
    const combinedRecipients = [
      ...selectedContacts.map(id => `contact:${id}`),
      ...selectedGroups.map(id => `group:${id}`)
    ];
    
    if (combinedRecipients.length > 0) {
      reset({
        ...watch(),
        contactGroupIds: combinedRecipients
      });
    }
  }, [selectedContacts, selectedGroups, reset, watch]);

  // Helper functions for contact and group selection
  const isContactSelected = (contactId: string | number): boolean => {
    return selectedContacts.some(id => String(id) === String(contactId));
  };

  const isGroupSelected = (groupId: string | number): boolean => {
    return selectedGroups.some(id => String(id) === String(groupId));
  };

  const toggleContactSelection = (contactId: string | number) => {
    const stringId = String(contactId);
    if (isContactSelected(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => String(id) !== stringId));
    } else {
      setSelectedContacts([...selectedContacts, stringId]);
    }
  };

  const toggleGroupSelection = (groupId: string | number) => {
    const stringId = String(groupId);
    if (isGroupSelected(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => String(id) !== stringId));
    } else {
      setSelectedGroups([...selectedGroups, stringId]);
    }
  };

  const clearAllSelections = () => {
    setSelectedContacts([]);
    setSelectedGroups([]);
  };

  // Select all contacts function
  const selectAllContacts = () => {
    const allContactIds = filteredContacts.map(contact => String(contact.id));
    setSelectedContacts(allContactIds);
  };

  // Select all groups function
  const selectAllGroups = () => {
    const allGroupIds = filteredGroups.map(group => String(group.id));
    setSelectedGroups(allGroupIds);
  };

  const renderRecipientsSection = () => {
    return (
      <div className="mb-8 border-b pb-6">
        <h3 className="text-lg font-semibold mb-4">Select Recipients</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="contactGroupIds">
            Target Audience *
          </label>
          
          {/* Hidden field for form submission */}
          <Controller
            name="contactGroupIds"
            control={control}
            rules={{ 
              required: "You must select at least one recipient",
              validate: value => value.length > 0 || "You must select at least one recipient"
            }}
            render={({ field }) => (
              <input 
                type="hidden" 
                {...field}
              />
            )}
          />
          
          <div className="border rounded-lg overflow-hidden shadow-sm">
            {/* Search and filter header */}
            <div className="p-3 border-b bg-gray-50 flex items-center space-x-2">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border-gray-200 rounded-md text-sm focus:border-whatsapp-green focus:ring-whatsapp-green"
                  placeholder="Search by name or phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => selectedTab === 'contacts' ? selectAllContacts() : selectAllGroups()}
                  className="text-sm text-whatsapp-green hover:text-whatsapp-dark-green"
                >
                  Select all
                </button>
                {(selectedContacts.length > 0 || selectedGroups.length > 0) && (
                  <button
                    type="button"
                    onClick={clearAllSelections}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
            
            {/* Tab selector for Contacts/Groups */}
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => setSelectedTab('contacts')}
                className={`flex-1 py-2 px-4 text-center text-sm font-medium ${selectedTab === 'contacts' 
                  ? 'border-b-2 border-whatsapp-green text-whatsapp-green' 
                  : 'text-gray-500 hover:text-gray-700'}`}
              >
                Contacts {selectedContacts.length > 0 && `(${selectedContacts.length})`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedTab('groups')}
                className={`flex-1 py-2 px-4 text-center text-sm font-medium ${selectedTab === 'groups' 
                  ? 'border-b-2 border-whatsapp-green text-whatsapp-green' 
                  : 'text-gray-500 hover:text-gray-700'}`}
              >
                Groups {selectedGroups.length > 0 && `(${selectedGroups.length})`}
              </button>
            </div>
            
            {/* Contacts/Groups list */}
            <div className="overflow-y-auto max-h-64">
              {selectedTab === 'contacts' ? (
                <div className="divide-y">
                  {filteredContacts.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? 'No contacts match your search' : 'No contacts available'}
                    </div>
                  ) : (
                    filteredContacts.map((contact: any) => (
                      <div 
                        key={contact.id} 
                        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                          isContactSelected(contact.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleContactSelection(contact.id)}
                      >
                        <div className="flex-grow">
                          <div className="font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.phone_number}</div>
                          {contact.marketing_opt_out && (
                            <div className="mt-1 flex items-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                <svg className="-ml-0.5 mr-1 h-3 w-3 text-red-400" fill="currentColor" viewBox="0 0 8 8">
                                  <circle cx="4" cy="4" r="3" />
                                </svg>
                                Opted out of marketing
                              </span>
                            </div>
                          )}
                        </div>
                        <div>
                          <input 
                            type="checkbox" 
                            checked={isContactSelected(contact.id)}
                            onChange={() => {}} // Handled by the div onClick
                            className="h-5 w-5 text-whatsapp-green focus:ring-whatsapp-green rounded"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredGroups.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? 'No groups match your search' : 'No groups available'}
                    </div>
                  ) : (
                    filteredGroups.map((group: any) => (
                      <div 
                        key={group.id} 
                        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                          isGroupSelected(group.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleGroupSelection(group.id)}
                      >
                        <div className="flex-grow">
                          <div className="font-medium text-gray-900">{group.name}</div>
                          <div className="text-sm text-gray-500">
                            {group.memberCount || 0} contacts
                          </div>
                        </div>
                        <div>
                          <input 
                            type="checkbox" 
                            checked={isGroupSelected(group.id)}
                            onChange={() => {}} // Handled by the div onClick
                            className="h-5 w-5 text-whatsapp-green focus:ring-whatsapp-green rounded"
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Selected count footer */}
            <div className="p-3 border-t bg-gray-50 flex flex-col space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium">{recipientCount}</span> recipients selected
                </div>
                <div className="text-sm">
                  <span className="font-medium">{selectedContacts.length}</span> contacts, <span className="font-medium">{selectedGroups.length}</span> groups
                </div>
              </div>
              
              {/* Warning about opted-out contacts */}
              {selectedContacts.length > 0 && contacts && (
                (() => {
                  // Count how many selected contacts have opted out
                  const optedOutContacts = contacts.contacts
                    ?.filter(c => c.marketing_opt_out && selectedContacts.includes(String(c.id)))
                    ?.length || 0;
                    
                  return optedOutContacts > 0 ? (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 text-sm">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-yellow-700">
                            <strong>Warning:</strong> {optedOutContacts} selected contact{optedOutContacts > 1 ? 's have' : ' has'} opted out of marketing messages and will be skipped.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()
              )}
            </div>
          </div>
          
          {errors.contactGroupIds && (
            <p className="text-red-500 text-xs mt-1">{errors.contactGroupIds.message}</p>
          )}
          <p className="text-gray-500 text-xs mt-1">
            Select the contacts or groups who will receive this campaign
          </p>
        </div>
      </div>
    );
  };

  const renderSchedulingSection = () => {
    // Compute a human-friendly preview of the next run for recurring schedules
    const format = (d: Date) => d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const computeNextRun = (): string | null => {
      if (!isRecurring) return null;
      const now = new Date();
      const h = recurrenceHour ?? 9;
      const m = recurrenceMinute ?? 0;
      let next: Date | null = null;
      if (recurrenceType === 'daily') {
        next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
        if (next <= now) next.setDate(next.getDate() + 1);
      } else if (recurrenceType === 'weekly') {
        if (!recurrenceDays || recurrenceDays.length === 0) return null;
        // Find next selected weekday >= now
        const todayIdx = now.getDay(); // 0=Sun..6=Sat
        let minDelta: number | null = null;
        recurrenceDays.forEach((wd) => {
          const delta = (wd - todayIdx + 7) % 7;
          const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + delta, h, m, 0, 0);
          const isValid = candidate > now;
          const effectiveDelta = isValid ? delta : delta + 7;
          const d = (wd === todayIdx && isValid) ? 0 : effectiveDelta;
          if (minDelta === null || d < minDelta) minDelta = d;
        });
        const add = (minDelta ?? 0);
        next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + add, h, m, 0, 0);
        if (next <= now) next.setDate(next.getDate() + 7);
      } else if (recurrenceType === 'monthly') {
        if (!recurrenceDays || recurrenceDays.length === 0) return null;
        const daysSorted = [...recurrenceDays].sort((a,b)=>a-b);
        // Try current month days >= today first
        for (const d of daysSorted) {
          const candidate = new Date(now.getFullYear(), now.getMonth(), d, h, m, 0, 0);
          if (!isNaN(candidate.getTime()) && candidate > now) { next = candidate; break; }
        }
        if (!next) {
          // Move to next month and pick earliest valid day
          const y = now.getFullYear();
          const mon = now.getMonth() + 1;
          for (const d of daysSorted) {
            const candidate = new Date(y, mon, d, h, m, 0, 0);
            if (!isNaN(candidate.getTime())) { next = candidate; break; }
          }
          // Fallback to first of next month
          if (!next) next = new Date(y, mon, 1, h, m, 0, 0);
        }
      }
      return next ? format(next) : null;
    };
    const nextRunText = computeNextRun();

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">Campaign Scheduling</h3>
        
        {/* Mark as Scheduled toggle */}
        <div className="mb-6">
          <div className={`flex items-center justify-between p-4 rounded-lg border ${scheduleImmediately ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
            <div>
              <h4 className={`font-medium ${scheduleImmediately ? 'text-green-800' : 'text-gray-800'}`}>Mark as Scheduled</h4>
              <p className={`text-sm ${scheduleImmediately ? 'text-green-700' : 'text-gray-600'}`}>
                {scheduleImmediately ? "Campaign will be saved as 'scheduled'. You can set a schedule date/time below." : 'Leave off to save as draft.'}
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer select-none">
              <input 
                type="checkbox" 
                className="sr-only"
                checked={scheduleImmediately}
                onChange={(e) => setScheduleImmediately(e.target.checked)}
                aria-label="Mark as Scheduled"
              />
              <div className={`relative w-12 h-7 rounded-full transition-colors ${scheduleImmediately ? 'bg-green-600' : 'bg-gray-300'}`}>
                <span className={`absolute top-[3px] left-[3px] h-5 w-5 rounded-full bg-white shadow transition-transform ${scheduleImmediately ? 'translate-x-5' : ''}`}></span>
              </div>
            </label>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Schedule Type
          </label>
          
          {/* Scheduling options */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <input
                id="one-time"
                type="radio"
                name="schedule-type"
                className="mr-2"
                checked={!isRecurring}
                onChange={() => {
                  setIsRecurring(false);
                }}
              />
              <label htmlFor="one-time" className="text-sm text-gray-700">One-time campaign</label>
            </div>
            
            <div className="flex items-center mb-2">
              <input
                id="recurring"
                type="radio"
                name="schedule-type"
                className="mr-2"
                checked={isRecurring}
                onChange={() => {
                  setIsRecurring(true);
                }}
              />
              <label htmlFor="recurring" className="text-sm text-gray-700">Recurring campaign</label>
            </div>
          </div>
          
          {!isRecurring ? (
            // One-time schedule
            <div className="bg-gray-50 border rounded-md p-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">Schedule Date & Time</label>
              <Controller
                name="scheduledAt"
                control={control}
                render={({ field }) => (
                  <DateTimeInlinePicker
                    value={field.value}
                    onChange={field.onChange}
                    minuteStep={1}
                  />
                )}
              />
              {scheduleImmediately ? (
                <p className="text-green-700 text-xs mt-1">
                  If left blank, this will be scheduled immediately (now).
                </p>
              ) : (
                <p className="text-gray-500 text-xs mt-1">
                  Use the Clear button to remove the date and save as draft without scheduling
                </p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Using your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
              </p>
            </div>
          ) : (
            // Recurring schedule options
            <div className="bg-gray-50 border rounded-md p-4">
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-medium mb-2">Recurrence</label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  value={recurrenceType}
                  onChange={(e) => setRecurrenceType(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              {/* Show day selection for weekly recurrence */}
              {recurrenceType === 'weekly' && (
                <div className="mb-3">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Days of week</label>
                  {/* Presets */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50"
                      onClick={() => setRecurrenceDays([1,2,3,4,5])}
                    >Weekdays</button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50"
                      onClick={() => setRecurrenceDays([0,6])}
                    >Weekends</button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50"
                      onClick={() => setRecurrenceDays([0,1,2,3,4,5,6])}
                    >Every day</button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50"
                      onClick={() => setRecurrenceDays([])}
                    >Clear</button>
                  </div>
                  {/* Day chips */}
                  <div className="grid grid-cols-7 gap-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                      const selected = recurrenceDays.includes(index);
                      return (
                        <button
                          type="button"
                          key={day}
                          onClick={() => {
                            const newDays = selected
                              ? recurrenceDays.filter(d => d !== index)
                              : [...recurrenceDays, index];
                            setRecurrenceDays(newDays);
                          }}
                          className={`px-2 py-1 rounded text-xs border transition-colors ${selected ? 'bg-whatsapp-green text-white border-whatsapp-green' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                          aria-pressed={selected}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Show day selection for monthly recurrence */}
              {recurrenceType === 'monthly' && (
                <div className="mb-3">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Days of month</label>
                  {/* Presets */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button type="button" className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50" onClick={() => setRecurrenceDays([1])}>1st</button>
                    <button type="button" className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50" onClick={() => setRecurrenceDays([15])}>15th</button>
                    <button type="button" className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50" onClick={() => setRecurrenceDays([1,15])}>1st & 15th</button>
                    <button type="button" className="px-3 py-1.5 rounded border text-xs hover:bg-gray-50" onClick={() => setRecurrenceDays([])}>Clear</button>
                  </div>
                  {/* Day grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                      const selected = recurrenceDays.includes(day);
                      return (
                        <button
                          type="button"
                          key={day}
                          onClick={() => {
                            const newDays = selected
                              ? recurrenceDays.filter(d => d !== day)
                              : [...recurrenceDays, day];
                            setRecurrenceDays(newDays);
                          }}
                          className={`h-9 rounded text-sm border transition-colors ${selected ? 'bg-whatsapp-green text-white border-whatsapp-green' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                          aria-pressed={selected}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-medium mb-2">Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</label>
                <InlineTimePicker
                  hour={recurrenceHour}
                  minute={recurrenceMinute}
                  minuteStep={5}
                  onChange={(h, m) => { setRecurrenceHour(h); setRecurrenceMinute(m); }}
                />
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">End date (optional)</label>
                <input
                  type="date"
                  min={new Date().toISOString().slice(0,10)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-whatsapp-green"
                  {...register("recurrenceEndDate")}
                />
                <p className="text-gray-500 text-xs mt-1">
                  Leave blank to run indefinitely
                </p>
              </div>

              {/* Next run preview */}
              <div className="mt-3 p-3 rounded border border-green-200 bg-green-50">
                {recurrenceType === 'weekly' && recurrenceDays.length === 0 ? (
                  <p className="text-sm text-green-800">Select at least one weekday to see the next run.</p>
                ) : recurrenceType === 'monthly' && recurrenceDays.length === 0 ? (
                  <p className="text-sm text-green-800">Select at least one day of month to see the next run.</p>
                ) : nextRunText ? (
                  <p className="text-sm text-green-800">Next run: {nextRunText}</p>
                ) : (
                  <p className="text-sm text-green-800">Next run will be calculated based on your selections.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Load campaign data when editing
  useEffect(() => {
    if (isEditing && campaignData) {
      console.log('Loading campaign data for editing:', campaignData);
      
      // Parse existing recipients into contacts and groups
      const contacts: string[] = [];
      const groups: string[] = [];
      
      // First check if we have detailed contact information
      if ((campaignData as any).contact_details && (campaignData as any).contact_details.length > 0) {
        (campaignData as any).contact_details.forEach((detail: any) => {
          if (detail.id && typeof detail.id === 'string') {
            if (detail.type === 'contact') {
              // Extract ID number from contact:ID format
              const contactId = detail.id.replace('contact:', '');
              contacts.push(contactId);
            } else if (detail.type === 'group') {
              // Extract ID number from group:ID format
              const groupId = detail.id.replace('group:', '');
              groups.push(groupId);
            }
          }
        });
      }
      // Fallback to contactGroupIds if contact_details is not available
      else if ((campaignData as any).contactGroupIds && (campaignData as any).contactGroupIds.length > 0) {
        (campaignData as any).contactGroupIds.forEach((id: string) => {
          if (typeof id === 'string') {
            if (id.startsWith('contact:')) {
              contacts.push(id.replace('contact:', ''));
            } else if (id.startsWith('group:')) {
              groups.push(id.replace('group:', ''));
            } else {
              // Legacy format - assume it's a group ID
              groups.push(id);
            }
          }
        });
      }
      
      // Set selected contacts and groups
      setSelectedContacts(contacts);
      setSelectedGroups(groups);
      
      // Get template ID properly - check both template.id and templateId fields
      let templateId = '';
      if (campaignData.template && campaignData.template.id) {
        templateId = campaignData.template.id.toString();
      } else if ((campaignData as any).templateId) {
        templateId = (campaignData as any).templateId.toString();
      } else if (campaignData.template_id) {
        templateId = campaignData.template_id.toString();
      }
      
      // Set selected template and trigger preview
      if (templateId) {
        setSelectedTemplateId(templateId);
        const selectedTemplate = templates?.find((t: any) => t.id.toString() === templateId);
        if (selectedTemplate) {
          const variables = extractTemplateVariables(selectedTemplate);
          setTemplatePreview({
            ...selectedTemplate,
            variables
          });
        }
      }
      
      // Handle recurring campaign fields
      const isRecurringCampaign = (campaignData as any).is_recurring === 1 || (campaignData as any).isRecurring === 1;
      if (isRecurringCampaign) {
        setIsRecurring(true);
        
        // Handle recurrence type - check both naming conventions
        const recType = (campaignData as any).recurrence_type || (campaignData as any).recurrenceType || 'daily';
        setRecurrenceType(recType);
        
        // Set recurrence days if present - check both naming conventions
        const recDays = (campaignData as any).recurrence_days || (campaignData as any).recurrenceDays || [];
        if (recDays && Array.isArray(recDays)) {
          setRecurrenceDays(recDays);
        }
        
        // Set time - check both naming conventions
        const hour = (campaignData as any).recurrence_hour !== undefined ? (campaignData as any).recurrence_hour : 
                    ((campaignData as any).recurrenceHour !== undefined ? (campaignData as any).recurrenceHour : 9);
        const minute = (campaignData as any).recurrence_minute !== undefined ? (campaignData as any).recurrence_minute : 
                      ((campaignData as any).recurrenceMinute !== undefined ? (campaignData as any).recurrenceMinute : 0);
        
        setRecurrenceHour(hour);
        setRecurrenceMinute(minute);
        
        // Reset form with campaign data including recurring settings
        reset({
          name: campaignData.name,
          description: (campaignData as any).description || '',
          templateId: templateId,
          contactGroupIds: contacts.map(id => `contact:${id}`).concat(groups.map(id => `group:${id}`)),
          isRecurring: 1,
          recurrenceType: recType,
          recurrenceDays: recDays,
          recurrenceHour: hour,
          recurrenceMinute: minute,
          recurrenceEndDate: ((campaignData as any).recurrence_end_date || (campaignData as any).recurrenceEndDate) ? 
            new Date((campaignData as any).recurrence_end_date || (campaignData as any).recurrenceEndDate).toISOString().slice(0, 10) : undefined
        });
      } else {
        // One-time campaign
        setIsRecurring(false);
        
        // Reset form with campaign data
        reset({
          name: campaignData.name,
          description: (campaignData as any).description || '',
          templateId: templateId,
          scheduledAt: campaignData.scheduled_at ? new Date(campaignData.scheduled_at).toISOString().slice(0, 16) : undefined,
          contactGroupIds: contacts.map(id => `contact:${id}`).concat(groups.map(id => `group:${id}`)),
          isRecurring: 0
        });
      }

      // Prefill schedule toggle based on status
      setScheduleImmediately(((campaignData as any).status || '').toLowerCase() === 'scheduled');

      // Prefill media handle if present
      if ((campaignData as any).media_id) {
        setMediaHandle((campaignData as any).media_id);
      } else if ((campaignData as any).media_handle) {
        setMediaHandle((campaignData as any).media_handle);
      }
    }
  }, [isEditing, campaignData, templates, reset]);

  const onSubmit = async (data: CampaignFormData) => {
    try {
      // Format recipient IDs to ensure compatibility with backend
      const formattedContactGroupIds = data.contactGroupIds.map(id => {
        // Ensure all IDs have proper prefix format
        if (id.startsWith('contact:') || id.startsWith('group:')) {
          return id; // Keep existing format
        }
        return `group:${id}`; // Add prefix for legacy IDs
      }).filter(id => id); // Remove any empty IDs

      // Make sure we have the template ID as a number
      const templateId = data.templateId ? parseInt(data.templateId.toString()) : null;

      // Create a data object that can accept any property name for backend
      const backendData: Record<string, any> = {
        // Copy basic fields from form data
        id: isEditing ? parseInt(id!) : undefined,
        name: data.name,
        description: data.description,
        templateId: templateId, // Ensure template ID is sent as number
        contactGroupIds: formattedContactGroupIds,
        status: isEditing ? campaignData?.status : 'draft',
      };
      
      // Add WhatsApp media ID if provided (use media_id as expected by backend)
      if (mediaHandle) {
        backendData.media_id = mediaHandle;
      }
      
      // Handle recurring campaign settings
      if (isRecurring) {
        // Add recurring campaign fields in snake_case for backend
        backendData.is_recurring = 1;
        backendData.recurrence_type = recurrenceType;
        
        // Only include days for weekly and monthly recurrence
        if (recurrenceType === 'weekly' || recurrenceType === 'monthly') {
          backendData.recurrence_days = recurrenceDays.length > 0 ? recurrenceDays : 
            (recurrenceType === 'weekly' ? [1] : [1]); // Default to Monday for weekly or 1st for monthly
        }
        
        // Add time settings
        backendData.recurrence_hour = recurrenceHour;
        backendData.recurrence_minute = recurrenceMinute;
        
        // Add end date if provided
        if (data.recurrenceEndDate) {
          backendData.recurrence_end_date = new Date(data.recurrenceEndDate).toISOString();
        }
      } else {
        // For one-time campaigns, handle scheduledAt with timezone info
        if (data.scheduledAt) {
          // Create date object from input
          const scheduledDate = new Date(data.scheduledAt);
          
          // Add timezone information to the ISO string 
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          backendData.scheduled_at = scheduledDate.toISOString();
          backendData.timezone = userTimezone;
        } else {
          backendData.scheduled_at = undefined;
        }
        
        backendData.is_recurring = 0;
      }
      
      // Log the data being prepared for the backend
      console.log('Preparing backend data:', backendData);

      // If schedule immediately is selected, mark as scheduled (previous behavior) and ensure dates
      if (scheduleImmediately) {
        backendData.status = 'scheduled';
        if (isRecurring) {
          const now = new Date();
          if (!backendData.recurrence_start_date) {
            backendData.recurrence_start_date = now.toISOString();
          }
        } else {
          // For one-time campaigns, set scheduled_at to now if not already set
          if (!backendData.scheduled_at) {
            const now = new Date();
            backendData.scheduled_at = now.toISOString();
            backendData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          }
        }
      }

      console.log('Final data being sent to backend:', backendData);

      if (isEditing) {
        await updateCampaign(backendData).unwrap();
        showNotification('success', `Campaign "${data.name}" updated successfully`);
        if (scheduleImmediately) {
          showNotification('info', `Campaign "${data.name}" has been scheduled to run`);
        }
      } else {
        await createCampaign(backendData).unwrap();
        showNotification('success', `Campaign "${data.name}" created successfully`);
        if (scheduleImmediately) {
          showNotification('info', `Campaign "${data.name}" has been scheduled to run`);
        }
      }
      
      // Navigate back to campaigns list
      navigate('/campaigns');
      
    } catch (error: any) {
      console.error('Error saving campaign', error);
      showNotification('error', `Failed to save campaign: ${error?.data?.detail || error?.message || 'Unknown error'}`);
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-whatsapp-teal">
            {isEditing ? 'Edit Campaign' : 'Create New Campaign'}
          </h1>
          <button
            onClick={() => navigate('/campaigns')}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Campaigns
          </button>
        </div>

        {(isCampaignLoading || isTemplatesLoading) ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg p-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Campaign Details Section */}
              <div className="mb-6 border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Campaign Details</h3>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="block w-full rounded-md border-gray-300 bg-white py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-whatsapp-green focus:border-whatsapp-green transition"
                    placeholder="e.g., September Promotion"
                    {...register("name", { required: "Campaign name is required" })}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                    Description
                  </label>
                  <textarea
                    id="description"
                    className="block w-full rounded-md border-gray-300 bg-white py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-whatsapp-green focus:border-whatsapp-green transition h-24"
                    placeholder="Describe the purpose of this campaign..."
                    {...register("description")}
                  />
                </div>
              </div>
              
              {/* Template Selection Section - Placeholder */}
              {renderTemplateSelectionSection()}
              
               {/* Recipients Selection Section - Placeholder */}
               {renderRecipientsSection()}

               {/* Cost Estimation Section */}
               {campaignCost && (
                 <div className="mb-8 border-b pb-6">
                   <h3 className="text-lg font-semibold mb-4">Estimated Cost</h3>
                   <div className="border rounded-lg p-4 bg-blue-50">
                     <div className="flex items-center justify-between mb-3">
                       <span className="text-sm font-medium text-gray-700">Total Estimated Cost:</span>
                       <span className={`text-lg font-bold ${campaignCost.totalCost > 50 ? 'text-red-600' : 'text-green-600'}`}>
                         ${campaignCost.totalCost.toFixed(2)}
                       </span>
                     </div>

                     {/* Cost breakdown by country */}
                     <div className="border-t pt-3">
                       <h4 className="text-sm font-medium text-gray-700 mb-2">Cost Breakdown by Country:</h4>
                       <div className="space-y-1">
                         {Object.entries(campaignCost.breakdown).map(([countryCode, data]) => (
                           <div key={countryCode} className="flex justify-between text-xs">
                             <span className="text-gray-600">
                               {countryCode === 'default' ? 'Other countries' : `Country +${countryCode}`}
                             </span>
                             <span className="text-gray-800">
                               {data.count} contacts × ${(WHATSAPP_PRICING[countryCode] || WHATSAPP_PRICING.default).toFixed(4)} = ${data.cost.toFixed(2)}
                             </span>
                           </div>
                         ))}
                       </div>
                     </div>

                     {/* Cost warning for high costs */}
                     {campaignCost.totalCost > 50 && (
                       <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                         <strong>High cost alert:</strong> This campaign may incur significant charges. Consider segmenting your audience or using templates to reduce costs.
                       </div>
                     )}
                   </div>
                   <p className="text-gray-500 text-xs mt-1">
                     Cost estimates are based on WhatsApp Business API pricing per conversation and may vary slightly
                   </p>
                 </div>
               )}

               {/* Campaign Scheduling Section - Placeholder */}
               {renderSchedulingSection()}
              
              <div className="flex justify-end mt-8">
                <button
                  type="button"
                  onClick={() => navigate('/campaigns')}
                  className="bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 font-semibold py-2 px-4 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-semibold py-2 px-4 rounded flex items-center"
                >
                  {(isCreating || isUpdating) && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isEditing ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default CampaignCreate;
